import BlurLinearOutlined from "@mui/icons-material/BlurLinearOutlined";
import { Button, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { isEmpty, uniqWith } from "lodash-es";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { CrudDataGrid } from "../../generic/components/crud-data-grid/crud-data-grid";
import { RelatedMomentsDialog } from "../../generic/components/crud-data-grid/related-moments-dialog";
import { SupabaseAccountPersonFieldService } from "../server/supabase-account-person-field-service";
import { eventKeyValidator } from "../types";

import type { Database } from "../../../__definitions__/database-definitions";
import type { UUID } from "../../../utils/uuid";
import type yup from "../../../utils/yup";
import type { RelatedMoments } from "../../generic/components/crud-data-grid/related-moments-dialog";
import type { Columns, Row } from "../../generic/components/crud-data-grid/types";
import type { FC } from "react";

type EventRow = Database["public"]["Views"]["events_with_related_moments_person"]["Row"] & {
  id: UUID;
  moments: { id: UUID; title: string }[];
};

export const EventCrudDataGridPerson: FC<{
  events: Database["public"]["Views"]["events_with_related_moments_person"]["Row"][];
}> = ({ events }) => {
  const dialog = useDialog<RelatedMoments>();

  const processedEvents: EventRow[] = uniqWith(
    events,
    (currentVal, otherVal) => currentVal.key === otherVal.key,
  ).map((datum) => ({
    ...datum,
    id: datum.id!,
    key: datum.key,
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    moments: datum.moments as { id: UUID; title: string }[],
  }));

  const onUpdate = useSupabaseCallback(async ({ supabase }, event: EventRow) => {
    const { data, error } = await new SupabaseAccountPersonFieldService(supabase).update(event.id, {
      allow_initial: event.allow_initial,
      allow_repeats: event.allow_repeats,
    });

    if (!data || error) {
      toast.error("Something went wrong");
      return;
    }
    toast.success("Saved");
    return event;
  }, []);

  const onAdd = useSupabaseCallback(async ({ supabase }, event: EventRow) => {
    const { data, error } = await new SupabaseAccountPersonFieldService(supabase).insert({
      key: `event_${event.key}`,
      display_name: event.key ?? "",
      merge_sample: "December 25th",
      connection_merge_sample: "December 25th",
      merge_field: true,
      connection_merge_field: false,
      channel_merge_field: true,
      segmentation_field: false,
      allow_initial: event.allow_initial,
      allow_repeats: event.allow_repeats,
      field_type: "DATE",
    });
    if (!data || error) {
      toast.error("Something went wrong");
      return;
    }
    toast.success("Saved");
    return {
      ...event,
      id: data.id,
    };
  }, []);

  const columns: Columns<EventRow> = {
    key: {
      headerName: "Key/Merge Tag",
      flex: 2,
      editable: true,
      options: {
        validator: async (value): Promise<string | undefined> =>
          await eventKeyValidator
            .validate(value)
            .then(() => undefined)
            .catch((error: yup.ValidationError) => error.errors.join("\n")),
      },
    },
    allow_initial: {
      headerName: "Allow Initial",
      type: "boolean",
      editable: true,
    },
    allow_repeats: {
      headerName: "Allow Repeats",
      type: "boolean",
      editable: true,
    },
    moments: {
      headerName: "Related Communications",
      editable: false,
      renderCell: (params) => {
        const numMoments = params.row.moments?.length ?? 0;
        return (
          <Button
            data-analytics-id="person-event-table-related-moments"
            onClick={() =>
              dialog.handleOpen({ title: params.row.key || "", moments: params.row.moments })
            }
            disabled={numMoments === 0}
          >
            {`${numMoments} Communications`}
          </Button>
        );
      },
    },
    events: {
      headerName: "Related People",
      editable: false,
      type: "number",
    },
  };

  return (
    <>
      <CrudDataGrid<Row<EventRow>>
        initialRows={processedEvents}
        columns={columns}
        initialData={{
          key: "",
          allow_initial: true,
          allow_repeats: true,
        }}
        onUpdate={onUpdate}
        onAdd={onAdd}
        gridOverrides={{
          isCellEditable: (params) => {
            const row: Row<EventRow> = params.row;
            if (row.options?.preventEditing) return false;
            // Only allow setting `key` on new fields or those without a value,
            // no editing of existing keys.
            if (params.colDef?.field === "key") return Boolean(row.isNew || isEmpty(row.key));
            return true;
          },
        }}
        modifyActions={(actions, row) => [
          ...actions,
          ...(row
            ? [
                <GridActionsCellItem
                  key={`timeline${row.id}`}
                  icon={
                    <Tooltip title="View Timeline">
                      <BlurLinearOutlined />
                    </Tooltip>
                  }
                  label="timeline"
                  onClick={() => {
                    row.key && window.open(`/timeline/${row.key}`, "_blank");
                  }}
                  disabled={row.moments ? row.moments.length === 0 : true}
                />,
              ]
            : []),
        ]}
      />
      <RelatedMomentsDialog subjectNamedInTitle="event" dialog={dialog} />
    </>
  );
};
